import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RegistrationState, NewUserRegistration } from '../../types/registration';
import { createUser, resetRegistration } from '../actions/registrationActions';

const initialState: RegistrationState = {
  newUser: {},
  currentStep: 1,
  totalSteps: 7, 
  completedSteps: [],
  isLoading: false,
  error: null,
};

const registrationSlice = createSlice({
  name: 'registration',
  initialState,
  reducers: {
    updateRegistrationField: (
      state,
      action: PayloadAction<{ field: keyof NewUserRegistration; value: any }>
    ) => {
      const { field, value } = action.payload;
      state.newUser[field] = value as never;
    },

    updateRegistrationData: (state, action: PayloadAction<Partial<NewUserRegistration>>) => {
      state.newUser = { ...state.newUser, ...action.payload };
    },

    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },

    nextStep: (state) => {
      if (state.currentStep < state.totalSteps) {
        if (!state.completedSteps.includes(state.currentStep)) {
          state.completedSteps.push(state.currentStep);
        }
        state.currentStep += 1;
      }
    },

    previousStep: (state) => {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
      }
    },

    markStepCompleted: (state, action: PayloadAction<number>) => {
      const step = action.payload;
      if (!state.completedSteps.includes(step)) {
        state.completedSteps.push(step);
      }
    },

    clearRegistrationError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
        state.newUser = {};
        state.currentStep = 1;
        state.completedSteps = [];
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || { 
          message: 'Registration failed', 
          code: 'REGISTRATION_ERROR' 
        };
      })
      .addCase(resetRegistration.fulfilled, (state) => {
        state.newUser = {};
        state.currentStep = 1;
        state.completedSteps = [];
        state.error = null;
      });
  },
});

export const {
  updateRegistrationField,
  updateRegistrationData,
  setCurrentStep,
  nextStep,
  previousStep,
  markStepCompleted,
  clearRegistrationError,
} = registrationSlice.actions;

export default registrationSlice.reducer;
